import React, { useEffect, useMemo, useRef, useState } from 'react';
import BRAND from '../brandConfig';
import { useApp } from '../context/AppContext';
import Wordmark from '../components/Wordmark';
import ThemeToggle from '../components/ThemeToggle';
import LanguageSwitcher from '../components/LanguageSwitcher';
import { arrangeWork, coverOf, pickGroup } from '../lib/portfolioGroups';

// ═══════════════════════════════════════════════════════════════════════════
//  OUR WORK — the portfolio page
//
//  It used to be four photo slots halfway down the homepage, hard-coded in
//  brandConfig.js. Julian asked for a page of its own he can fill with named
//  portfolios, because a caller asking about a deck wants to see decks, and
//  a homepage carrying every job QPS has ever finished is a homepage nobody
//  reaches the bottom of.
//
//  Three things this page has to get right:
//
//    · Each portfolio has its own address. qpshub.com/work/decks is a link
//      Julian can text to somebody who asked about a deck, and it opens
//      showing decks rather than showing everything and asking them to
//      filter. That is the whole reason the page exists.
//
//    · An empty page is still a page. On the day this ships there is nothing
//      in it. It says so plainly and offers the way forward, rather than
//      drawing an empty grid or, worse, inventing three projects.
//
//    · Nothing here is a claim. Photos and captions Julian wrote, and a city.
//      No counts, no costs, no durations. Public FIGURES have to be
//      sourceable — see the provenance notes in brandConfig.js — and a
//      portfolio page is the easiest place in the app to start making them up.
//
//  The arranging is in lib/portfolioGroups.js, which is tested. This file is
//  layout.
// ═══════════════════════════════════════════════════════════════════════════

export default function Work() {
  const { currentScreen, currentUser, goTo, workProjects, workLoaded } = useApp();
  const active = currentScreen === 'screen-work';

  // Which portfolio is showing. '' means all of them.
  const [filter, setFilter] = useState('');
  // The project opened full size, or null.
  const [open, setOpen] = useState(null);
  const [frame, setFrame] = useState(0);
  // Whether the open viewer has a history entry of its own behind it. On a
  // phone, Back is how people close a photo, and without an entry to consume
  // it takes them off the site instead.
  const viewerPushed = useRef(false);

  const { groups, all } = useMemo(() => arrangeWork(workProjects), [workProjects]);

  const filterFromPath = () => {
    const m = window.location.pathname.toLowerCase().match(/^\/work\/([^/]+)\/?$/);
    return m ? m[1] : '';
  };

  // The address decides what is showing, so a link to /work/decks opens on
  // decks. Read on arrival and whenever the page is shown again, because the
  // screens here are siblings that get hidden rather than unmounted.
  useEffect(() => {
    if (!active) return;
    setFilter(filterFromPath());
  }, [active]);

  // Back has two jobs on this page, in this order: close the open photo, then
  // step back through the portfolios that were looked at.
  //
  // Stepping between portfolios does not change the SCREEN, only the address,
  // so App's own popstate handler sets screen-work again and nothing here
  // re-runs. Without this listener the address would change under a page that
  // carried on showing the previous portfolio.
  useEffect(() => {
    const onPop = () => {
      if (viewerPushed.current) {
        viewerPushed.current = false;
        setOpen(null);
        return;
      }
      setFilter(filterFromPath());
    };
    window.addEventListener('popstate', onPop);
    return () => window.removeEventListener('popstate', onPop);
  }, []);

  // Changing portfolio changes the address, so Back steps between portfolios
  // and the link in the address bar is always the link worth sending. The
  // screen itself does not change, so this pushes history directly instead of
  // going through goTo().
  const show = (slug) => {
    setFilter(slug);
    const path = slug ? `/work/${slug}` : '/work';
    if (window.location.pathname !== path) {
      window.history.pushState({ screen: 'screen-work' }, '', path);
    }
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const leaveFor = (screen) => { window.scrollTo({ top: 0 }); goTo(screen); };

  // A slug in the address that matches no portfolio shows everything rather
  // than an empty page. Somebody mistyped a link Julian sent them, and a
  // blank screen tells them nothing.
  //
  // pickGroup, not a plain find(): the group holding projects with no
  // portfolio name has an empty slug, and an empty filter means "show me
  // everything", so a bare lookup matched it and /work opened on the handful
  // of loose projects instead of the whole page. The rule is tested.
  const chosen = pickGroup(groups, filter);
  const showing = chosen ? chosen.projects : all;

  const photosOf = (p) => (p && Array.isArray(p.photos) ? p.photos.filter((x) => x && x.url) : []);

  const openAt = (project, i = 0) => {
    setOpen(project);
    setFrame(i);
    if (!viewerPushed.current) {
      viewerPushed.current = true;
      // Same address, one more entry: Back consumes this instead of the page.
      window.history.pushState({ screen: 'screen-work', viewer: true }, '', window.location.pathname);
    }
  };

  // Closing by × , Escape or a click outside goes through Back, so there is
  // one way out and no entry left stranded in front of the page.
  const close = () => {
    if (viewerPushed.current) window.history.back();
    else setOpen(null);
  };

  // Leaving for the request form from inside the viewer. Going Back first and
  // navigating second would race, so the viewer's entry is overwritten rather
  // than popped: Back from the request form then lands on this page with the
  // photo already closed, which is where somebody would expect to be.
  const discuss = () => {
    setOpen(null);
    if (viewerPushed.current) {
      viewerPushed.current = false;
      window.history.replaceState({ screen: 'screen-work' }, '', window.location.pathname);
    }
    leaveFor('screen-request');
  };

  // Arrow keys and Escape in the viewer. A portfolio is looked at with two
  // thumbs on a phone and with the keyboard on a desk, and a viewer that
  // traps you until you find the X is a viewer people close with Back.
  useEffect(() => {
    if (!open) return undefined;
    const shots = photosOf(open);
    const onKey = (e) => {
      if (e.key === 'Escape') { close(); return; }
      if (!shots.length) return;
      if (e.key === 'ArrowRight') setFrame((f) => (f + 1) % shots.length);
      if (e.key === 'ArrowLeft') setFrame((f) => (f - 1 + shots.length) % shots.length);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open]);

  const when = (iso) => {
    if (!iso) return '';
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return '';
    return d.toLocaleDateString(undefined, { month: 'long', year: 'numeric' });
  };

  const card = (p) => {
    const cover = coverOf(p);
    const count = photosOf(p).length;
    return (
      <button
        type="button"
        key={p.id}
        className={`qw-card${cover ? '' : ' empty'}`}
        onClick={() => openAt(p, 0)}
      >
        <span
          className="qw-card-media"
          style={cover ? { backgroundImage: `url(${cover.url})` } : undefined}
          {...(cover
            ? { role: 'img', 'aria-label': cover.caption || p.title }
            : { 'aria-hidden': 'true' })}
        >
          {!cover && <i>[ PHOTO ]</i>}
          {count > 1 && <em className="qw-count">{count} photos</em>}
        </span>
        <span className="qw-card-body">
          <b>{p.title}</b>
          {(p.location || p.completedOn) && (
            <i>{[p.location, when(p.completedOn)].filter(Boolean).join(' · ')}</i>
          )}
          {p.summary && <span>{p.summary}</span>}
        </span>
      </button>
    );
  };

  const shots = photosOf(open);
  const shot = shots[frame] || null;

  return (
    <div className={`screen ${active ? 'active' : ''}`}>
      <div className="qh qw">

        {/* ── Top rail: the same one as the homepage, so this reads as part
             of the site rather than as somewhere a link threw you. ─────── */}
        <nav className="qh-nav">
          <button type="button" className="qh-word" onClick={() => leaveFor('screen-home')} aria-label={BRAND.name.full}>
            <Wordmark />
          </button>
          <div className="qh-links">
            <button type="button" className="qh-link" onClick={() => leaveFor('screen-home')}>Home</button>
            <span className="qh-link qh-link-on">Our Work</span>
          </div>
          <div className="qh-navact">
            {currentUser && (
              <button type="button" className="qh-link" onClick={() => leaveFor(
                currentUser.role === 'admin' ? 'screen-admin-dashboard'
                  : currentUser.role === 'vendor' ? 'screen-vendor-dashboard'
                    : 'screen-homeowner-dashboard'
              )}>Dashboard</button>
            )}
            <button type="button" className="qh-btn qh-btn-sm" onClick={() => leaveFor('screen-request')}>
              Discuss Your Project
            </button>
            <ThemeToggle /><LanguageSwitcher />
          </div>
        </nav>

        {/* ── Head ─────────────────────────────────────────────────────── */}
        <header className="qw-head">
          <span className="qh-eyebrow">Completed projects</span>
          <h1 className="qh-h1 qh-disp">Our Work</h1>
          <p className="qh-lede">
            Finished jobs across {BRAND.location}, grouped by the kind of work. Every photo is
            a project QPS coordinated from the first conversation to the last walkthrough.
          </p>
        </header>

        {/* ── The portfolios ───────────────────────────────────────────── */}
        {groups.length > 1 && (
          <div className="qw-tabs" role="tablist" aria-label="Portfolios">
            <button
              type="button" role="tab" aria-selected={!chosen}
              className={`qw-tab${chosen ? '' : ' on'}`}
              onClick={() => show('')}
            >
              All work <i>{all.length}</i>
            </button>
            {groups.map((g) => (
              <button
                key={g.slug || 'other'} type="button" role="tab"
                aria-selected={chosen === g}
                className={`qw-tab${chosen === g ? ' on' : ''}`}
                onClick={() => show(g.slug)}
              >
                {g.name || 'Other projects'} <i>{g.projects.length}</i>
              </button>
            ))}
          </div>
        )}

        {/* ── The grid ─────────────────────────────────────────────────── */}
        <section className="qw-wrap">
          {!workLoaded ? (
            <p className="qw-note">Loading the work…</p>
          ) : showing.length === 0 ? (
            // Day one, and honestly so. Three invented projects is the one
            // lie a portfolio page cannot survive being caught in.
            <div className="qw-none">
              <h2 className="qh-h2 qh-disp">THE FIRST PROJECTS<br />ARE GOING UP.</h2>
              <p>
                Photographs of finished work are being added here as jobs close out. In the
                meantime, tell us about your property and we will walk you through what
                similar projects have involved.
              </p>
              <button type="button" className="qh-btn" onClick={() => leaveFor('screen-request')}>
                Discuss Your Project
              </button>
            </div>
          ) : chosen ? (
            <>
              <h2 className="qw-group-h qh-disp">{chosen.name || 'Other projects'}</h2>
              <div className="qw-grid">{showing.map(card)}</div>
            </>
          ) : (
            // Showing everything: still grouped, with the portfolio name as a
            // heading, so the page reads as a set of portfolios rather than as
            // one long pile of photos.
            groups.map((g) => (
              <div className="qw-group" key={g.slug || 'other'}>
                <div className="qw-group-top">
                  <h2 className="qw-group-h qh-disp">{g.name || 'Other projects'}</h2>
                  {g.slug && g.projects.length > 1 && (
                    <button type="button" className="qw-more" onClick={() => show(g.slug)}>
                      See all {g.projects.length} →
                    </button>
                  )}
                </div>
                <div className="qw-grid">{g.projects.map(card)}</div>
              </div>
            ))
          )}
        </section>

        {/* ── Closing ──────────────────────────────────────────────────── */}
        {showing.length > 0 && (
          <section className="qh-close">
            <h2 className="qh-h2 qh-disp">Thinking about<br />something similar?</h2>
            <button type="button" className="qh-btn-dark" onClick={() => leaveFor('screen-request')}>
              Discuss Your Project
            </button>
          </section>
        )}

        <footer className="qh-foot">
          <Wordmark className="qh-foot-word" />
          <span className="qh-foot-sp">
            {BRAND.company.website} · {BRAND.location} · © {BRAND.copyrightYear} {BRAND.company.legalName}
          </span>
          <button type="button" className="qh-foot-link" onClick={() => leaveFor('screen-home')}>Home</button>
          <button type="button" className="qh-foot-link" onClick={() => leaveFor('screen-privacy')}>Privacy Policy</button>
          <button type="button" className="qh-foot-link" onClick={() => leaveFor('screen-terms')}>Terms of Service</button>
        </footer>

      {/* ── The viewer ─────────────────────────────────────────────────── */}
      {/* Inside `.qh`, not beside it. The palette variables are declared on
          `.qh`, so a fixed overlay rendered as its sibling resolved
          --qh-canvas to nothing and drew the whole viewer transparent, with
          the page showing through the photograph. */}
      {open && (
        <div className="qw-view" role="dialog" aria-modal="true" aria-label={open.title} onClick={close}>
          <div className="qw-view-box" onClick={(e) => e.stopPropagation()}>
            <button type="button" className="qw-x" onClick={close} aria-label="Close">×</button>

            <div className="qw-stage">
              {shot ? (
                <img src={shot.url} alt={shot.caption || open.title} />
              ) : (
                <div className="qw-stage-empty">No photographs on this project yet.</div>
              )}
              {shots.length > 1 && (
                <>
                  <button
                    type="button" className="qw-arrow left" aria-label="Previous photo"
                    onClick={() => setFrame((f) => (f - 1 + shots.length) % shots.length)}
                  >‹</button>
                  <button
                    type="button" className="qw-arrow right" aria-label="Next photo"
                    onClick={() => setFrame((f) => (f + 1) % shots.length)}
                  >›</button>
                </>
              )}
            </div>

            <div className="qw-view-body">
              <b>{open.title}</b>
              {(open.location || open.completedOn || open.portfolio) && (
                <i>{[open.portfolio, open.location, when(open.completedOn)].filter(Boolean).join(' · ')}</i>
              )}
              {shot && shot.caption && <p className="qw-cap">{shot.caption}</p>}
              {open.summary && <p>{open.summary}</p>}
              {shots.length > 1 && (
                <div className="qw-strip">
                  {shots.map((s, i) => (
                    <button
                      type="button" key={s.url + i}
                      className={`qw-thumb${i === frame ? ' on' : ''}`}
                      style={{ backgroundImage: `url(${s.url})` }}
                      aria-label={`Photo ${i + 1} of ${shots.length}`}
                      onClick={() => setFrame(i)}
                    />
                  ))}
                </div>
              )}
              <button type="button" className="qh-btn" onClick={discuss}>
                Discuss Your Project
              </button>
            </div>
          </div>
        </div>
      )}
      </div>
    </div>
  );
}
